import { useState, useEffect } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

export const useNativeFeatures = () => {
  const [isNative, setIsNative] = useState(false);
  const [permissions, setPermissions] = useState({
    camera: false,
    location: false,
    notifications: false
  });

  useEffect(() => {
    setIsNative(Capacitor.isNativePlatform());
    
    if (Capacitor.isNativePlatform()) {
      initializeNativeFeatures();
    }
  }, []);

  const initializeNativeFeatures = async () => {
    // Request camera permissions
    try {
      const cameraStatus = await Camera.requestPermissions();
      setPermissions(prev => ({ ...prev, camera: cameraStatus.camera === 'granted' }));
    } catch (error) {
      console.error('Camera permission error:', error);
    }

    // Request location permissions
    try {
      const locationStatus = await Geolocation.requestPermissions();
      setPermissions(prev => ({ ...prev, location: locationStatus.location === 'granted' }));
    } catch (error) {
      console.error('Location permission error:', error);
    }

    // Request notification permissions
    try {
      const notificationStatus = await PushNotifications.requestPermissions();
      setPermissions(prev => ({ 
        ...prev, 
        notifications: notificationStatus.receive === 'granted' 
      }));

      if (notificationStatus.receive === 'granted') {
        await PushNotifications.register();
      }
    } catch (error) {
      console.error('Notification permission error:', error);
    }
  };

  const takePicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera
      });

      return image.dataUrl;
    } catch (error) {
      console.error('Camera error:', error);
      throw error;
    }
  };

  const getCurrentLocation = async () => {
    try {
      const coordinates = await Geolocation.getCurrentPosition();
      return {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy
      };
    } catch (error) {
      console.error('Location error:', error);
      throw error;
    }
  };

  const showLocalNotification = async (title: string, body: string) => {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            title,
            body,
            id: Date.now(),
            schedule: { at: new Date(Date.now() + 1000) },
            sound: 'default',
            attachments: undefined,
            actionTypeId: '',
            extra: null
          }
        ]
      });
    } catch (error) {
      console.error('Local notification error:', error);
      throw error;
    }
  };

  return {
    isNative,
    permissions,
    takePicture,
    getCurrentLocation,
    showLocalNotification,
    initializeNativeFeatures
  };
};